import { useState, useRef, FormEvent } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { supabase } from "../../lib/supabase";
import SEO from "@/components/SEO";

const FORM_URL = "https://readdy.ai/api/form/d89pi55f07jn52r5r430";

const tiposEspacio = [
  "Banner lateral (sidebar)",
  "Banner entre artículos",
  "Banner en página de Aeropuertos",
  "Banner en página de Aerolíneas",
  "Banner en página de Estadísticas",
  "Banner en página de Turismo",
  "Patrocinio de sección completa",
  "Artículo patrocinado",
  "Newsletter patrocinado",
  "Paquete completo (todas las páginas)",
];

const benefits = [
  {
    icon: "ri-focus-3-line",
    title: "Audiencia ultra-segmentada",
    desc: "Llega directamente a profesionales de aviación, ejecutivos del sector turístico, pilotos, controladores aéreos y entusiastas de la aviación dominicana e internacional.",
    color: "text-red-600",
    bg: "bg-red-50",
  },
  {
    icon: "ri-shield-star-line",
    title: "Credibilidad editorial",
    desc: "Aviación Insight RD es el portal de referencia del sector aeronáutico dominicano. Tu marca se asocia con contenido de calidad y autoridad en la industria.",
    color: "text-amber-600",
    bg: "bg-amber-50",
  },
  {
    icon: "ri-customer-service-2-line",
    title: "Atención directa y personalizada",
    desc: "Trabajarás directamente con el equipo de Aviación Insight RD. Sin intermediarios, sin burocracia. Respuesta garantizada en menos de 24 horas.",
    color: "text-orange-600",
    bg: "bg-orange-50",
  },
];

export default function Publicidad() {
  const [empresa, setEmpresa] = useState("");
  const [nombre, setNombre] = useState("");
  const [email, setEmail] = useState("");
  const [telefono, setTelefono] = useState("");
  const [tipoEspacio, setTipoEspacio] = useState("");
  const [mensaje, setMensaje] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!empresa || !nombre || !email || !tipoEspacio) {
      setError("Por favor completa todos los campos obligatorios.");
      return;
    }
    if (mensaje.length > 500) {
      setError("El mensaje no puede superar los 500 caracteres.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      // Guardar tambien en Supabase para que Carmen lo vea en el admin
      await supabase.from("advertising_requests").insert([
        {
          empresa,
          name: nombre,
          email,
          telefono,
          tipo_espacio: tipoEspacio,
          mensaje,
        },
      ]);

      const body = new URLSearchParams();
      body.append("empresa", empresa);
      body.append("name", nombre);
      body.append("email", email);
      body.append("telefono", telefono);
      body.append("tipo_espacio", tipoEspacio);
      body.append("mensaje", mensaje);
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
        formRef.current?.reset();
        setEmpresa(""); setNombre(""); setEmail(""); setTelefono("");
        setTipoEspacio(""); setMensaje("");
      } else {
        setError("Hubo un problema al enviar. Por favor intenta de nuevo.");
      }
    } catch {
      setError("Error de conexión. Verifica tu internet e intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#F4F7FB]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <SEO
        jsonLdSchemas={[
          {
            "@type": "WebPage",
            name: "Publicidad y Patrocinio",
            description: "Llega a la audiencia aeronáutica más influyente de República Dominicana.",
            url: 'https://aviacioninsightrd.com/publicidad',
          },
        ]}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative bg-[#0B1F3A] pt-32 pb-24 overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div
            className="w-full h-full"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)",
              backgroundSize: "48px 48px",
            }}
          />
        </div>
        <div className="max-w-screen-xl mx-auto px-4 md:px-6 relative z-10">
          <div className="flex items-center gap-2 mb-5">
            <Link to="/" className="text-blue-400 text-sm hover:text-blue-300 cursor-pointer">Inicio</Link>
            <i className="ri-arrow-right-s-line text-blue-600" />
            <span className="text-slate-400 text-sm">Publicidad</span>
          </div>
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 bg-red-600/20 border border-red-500/30 rounded-full px-3 py-1 w-fit mb-5">
              <i className="ri-megaphone-line text-red-400 text-xs" />
              <span className="text-red-300 text-[10px] font-bold tracking-widest uppercase">
                Anúnciate con nosotros
              </span>
            </div>
            <h1 className="text-white font-bold mb-5 leading-tight" style={{ fontFamily: "'Sora', sans-serif", fontSize: "clamp(2rem, 4vw, 3.4rem)" }}>
              LLEGA A LA AUDIENCIA AERONÁUTICA MÁS INFLUYENTE DE RD
            </h1>
            <p className="text-blue-200/80 text-base leading-relaxed mb-8 max-w-xl">
              Aviación Insight RD es el portal de referencia del sector aéreo dominicano. Conecta tu marca con profesionales, entusiastas y tomadores de decisiones de la industria.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="#form-publicidad"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-red-600 hover:bg-red-700 text-white font-bold text-sm rounded-xl transition-all cursor-pointer whitespace-nowrap"
                style={{ fontFamily: "'Sora', sans-serif" }}
              >
                <i className="ri-send-plane-line" />
                Solicitar información
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits section */}
      <section className="py-16 bg-[#F4F7FB]">
        <div className="max-w-screen-xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="h-px w-10 bg-slate-300" />
              <span className="text-slate-400 text-xs font-semibold tracking-widest uppercase">
                ¿Por qué anunciarte aquí?
              </span>
              <div className="h-px w-10 bg-slate-300" />
            </div>
            <h2 className="text-[#0B1F3A] font-extrabold mb-3" style={{ fontFamily: "'Sora', sans-serif", fontSize: "2rem" }}>
              Beneficios de anunciarte en Aviación Insight RD
            </h2>
            <p className="text-slate-400 text-sm max-w-lg mx-auto">
              No es solo publicidad — es conectar tu marca con la comunidad aeronáutica más activa del Caribe
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {benefits.map((b) => (
              <div key={b.title} className="bg-white rounded-xl border border-slate-100 p-6 hover:-translate-y-0.5 transition-transform">
                <div className={`w-11 h-11 flex items-center justify-center rounded-xl mb-4 ${b.bg}`}>
                  <i className={`${b.icon} text-xl ${b.color}`} />
                </div>
                <h3 className="text-[#0B1F3A] font-bold text-sm mb-2" style={{ fontFamily: "'Sora', sans-serif" }}>
                  {b.title}
                </h3>
                <p className="text-slate-500 text-xs leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form section */}
      <section id="form-publicidad" className="py-16 bg-white">
        <div className="max-w-screen-xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {/* Left info */}
            <div className="space-y-6">
              <div>
                <div className="flex items-center gap-2 bg-red-50 border border-red-100 rounded-full px-3 py-1 w-fit mb-4">
                  <i className="ri-megaphone-line text-red-500 text-xs" />
                  <span className="text-red-600 text-[10px] font-bold tracking-widest uppercase">Solicitud de publicidad</span>
                </div>
                <h2 className="text-[#0B1F3A] font-extrabold mb-2" style={{ fontFamily: "'Sora', sans-serif", fontSize: "1.8rem" }}>
                  Solicita tu espacio publicitario
                </h2>
                <p className="text-slate-500 text-sm leading-relaxed">
                  Completa el formulario y nos pondremos en contacto contigo en menos de 24 horas con una propuesta personalizada.
                </p>
              </div>

              <div className="bg-[#F4F7FB] rounded-xl border border-slate-100 p-5 space-y-4">
                {[
                  { icon: "ri-time-line", title: "Respuesta en 24h", sub: "Días hábiles" },
                  { icon: "ri-shield-check-line", title: "Propuesta personalizada", sub: "Adaptada a tu presupuesto" },
                ].map((item) => (
                  <div key={item.title} className="flex items-center gap-3">
                    <div className="w-9 h-9 flex items-center justify-center bg-white rounded-lg border border-slate-100 flex-shrink-0">
                      <i className={`${item.icon} text-[#0B1F3A] text-base`} />
                    </div>
                    <div>
                      <p className="text-[#0B1F3A] font-semibold text-sm">{item.title}</p>
                      <p className="text-slate-400 text-xs">{item.sub}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Form */}
            <div>
              <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
                <div className="bg-[#0B1F3A] px-4 md:px-8 py-6">
                  <h3 className="text-white font-bold text-xl" style={{ fontFamily: "'Sora', sans-serif" }}>
                    FORMULARIO DE PUBLICIDAD
                  </h3>
                  <p className="text-blue-300 text-sm mt-1">Los campos marcados con * son obligatorios</p>
                </div>

                {submitted ? (
                  <div className="px-4 md:px-8 py-16 text-center">
                    <div className="w-16 h-16 flex items-center justify-center bg-emerald-100 rounded-full mx-auto mb-5">
                      <i className="ri-check-double-line text-emerald-600 text-2xl" />
                    </div>
                    <h3 className="text-[#0B1F3A] font-bold text-xl mb-2" style={{ fontFamily: "'Sora', sans-serif" }}>
                      ¡Solicitud enviada con éxito!
                    </h3>
                    <p className="text-slate-500 text-sm max-w-sm mx-auto">
                      Gracias por tu interés en anunciarte con nosotros. Te pondremos en contacto en las próximas 24 horas hábiles con una propuesta personalizada.
                    </p>
                    <button
                      onClick={() => setSubmitted(false)}
                      className="mt-6 px-6 py-2.5 bg-[#0B1F3A] hover:bg-slate-800 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Enviar otra solicitud
                    </button>
                  </div>
                ) : (
                  <form
                    id="form-publicidad"
                    ref={formRef}
                    onSubmit={handleSubmit}
                    data-readdy-form
                    className="px-4 md:px-8 py-8 space-y-5"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-[#0B1F3A] font-semibold text-xs mb-2 uppercase tracking-wide">
                          Empresa / Marca *
                        </label>
                        <input
                          type="text"
                          name="empresa"
                          value={empresa}
                          onChange={(e) => setEmpresa(e.target.value)}
                          placeholder="Nombre de tu empresa"
                          className="w-full px-4 py-3 rounded-lg border border-slate-200 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]/30 focus:border-[#0B1F3A] transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-[#0B1F3A] font-semibold text-xs mb-2 uppercase tracking-wide">
                          Nombre de contacto *
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={nombre}
                          onChange={(e) => setNombre(e.target.value)}
                          placeholder="Tu nombre completo"
                          className="w-full px-4 py-3 rounded-lg border border-slate-200 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]/30 focus:border-[#0B1F3A] transition-all"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-[#0B1F3A] font-semibold text-xs mb-2 uppercase tracking-wide">
                          Correo electrónico *
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="tu@empresa.com"
                          className="w-full px-4 py-3 rounded-lg border border-slate-200 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]/30 focus:border-[#0B1F3A] transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-[#0B1F3A] font-semibold text-xs mb-2 uppercase tracking-wide">
                          Teléfono / WhatsApp
                        </label>
                        <input
                          type="tel"
                          name="telefono"
                          value={telefono}
                          onChange={(e) => setTelefono(e.target.value)}
                          placeholder="+1 (809) 000-0000"
                          className="w-full px-4 py-3 rounded-lg border border-slate-200 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]/30 focus:border-[#0B1F3A] transition-all"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-[#0B1F3A] font-semibold text-xs mb-2 uppercase tracking-wide">
                        Espacio de interés *
                      </label>
                      <select
                        name="tipo_espacio"
                        value={tipoEspacio}
                        onChange={(e) => setTipoEspacio(e.target.value)}
                        className="w-full px-4 py-3 rounded-lg border border-slate-200 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]/30 focus:border-[#0B1F3A] transition-all cursor-pointer bg-white"
                      >
                        <option value="">Selecciona un espacio</option>
                        {tiposEspacio.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-[#0B1F3A] font-semibold text-xs uppercase tracking-wide">
                          Cuéntanos sobre tu campaña
                        </label>
                        <span className={`text-xs ${mensaje.length > 450 ? "text-red-500" : "text-slate-400"}`}>
                          {mensaje.length}/500
                        </span>
                      </div>
                      <textarea
                        name="mensaje"
                        value={mensaje}
                        onChange={(e) => e.target.value.length <= 500 && setMensaje(e.target.value)}
                        placeholder="Describe tu producto o servicio, objetivos de la campaña, público objetivo, duración estimada..."
                        rows={5}
                        className="w-full px-4 py-3 rounded-lg border border-slate-200 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]/30 focus:border-[#0B1F3A] transition-all resize-none"
                      />
                    </div>

                    {error && (
                      <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                        <i className="ri-error-warning-line text-red-500 text-base" />
                        <p className="text-red-600 text-xs">{error}</p>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-4 bg-red-600 hover:bg-red-700 text-white font-bold text-sm rounded-xl transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 flex items-center justify-center gap-2"
                      style={{ fontFamily: "'Sora', sans-serif" }}
                    >
                      {loading ? (
                        <>
                          <i className="ri-loader-4-line animate-spin" />
                          Enviando solicitud...
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-line" />
                          Enviar Solicitud de Publicidad
                        </>
                      )}
                    </button>

                    <p className="text-slate-400 text-xs text-center">
                      Tu información es confidencial y nunca será compartida con terceros.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}